import IconButton from '@/global/components/Button/IconButton'
import { Cartao } from '@/global/components/Cartao'
import {
    FormField,
    FormFieldMask,
    FormFieldSelect,
} from '@/global/components/Form'
import { useAuth } from '@/hooks'
import { cartoes } from '@/service/api'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Alert, AlertTitle, Grid, Tab } from '@mui/material'
import {
    CreditCardIcon,
    ScanBarcodeIcon,
    Trash2Icon,
    WalletIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import useCheckoutForm from '../../hooks/useCheckoutForm'

interface IProps {
    disabled?: 'cartao' | 'boleto'
}

/**
 * * Utiliza `Customer.myId` para buscar os cartões salvos do cliente
 * * Salva os dados do cartão no campo `Card.myId`
 * * Se o cartão for novo, salva os dados do cartão no campo `Card`
 * * Se o método de pagamento for boleto, altera o campo `mainPaymentMethodId` para `'boleto'`
 */
export default function AbasFormaPagamento({ disabled }: IProps) {
    const { setValue } = useCheckoutForm()

    const AbaCartoes = useAbaCartoes(),
        AbaNovoCartao = useAbaNovoCartao(),
        AbaBoleto = useAbaBoleto()

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: string,
    ) => {
        setValue('Card.myId', '')
        setTab(newValue)
    }

    const [tab, setTab] = useState<string>(
        disabled === 'boleto' ? AbaNovoCartao.name : AbaBoleto.name,
    )

    useEffect(() => {
        if ([AbaCartoes.name, AbaNovoCartao.name].includes(tab as any))
            setValue('mainPaymentMethodId', 'creditcard')
        else setValue('mainPaymentMethodId', 'boleto')
    }, [tab])

    useEffect(() => {
        if (!AbaCartoes.visible && tab === AbaCartoes.name) {
            setTab(AbaNovoCartao.name)
        }
    }, [AbaCartoes.visible])

    return (
        <TabContext value={tab}>
            <div className="border-b">
                <TabList onChange={handleTabChange}>
                    {disabled !== 'cartao' &&
                        AbaCartoes.visible &&
                        AbaCartoes.Select}
                    {disabled !== 'cartao' && AbaNovoCartao.Select}
                    {disabled !== 'boleto' && AbaBoleto.Select}
                </TabList>
            </div>

            {disabled !== 'cartao' && AbaCartoes.Aba}
            {disabled !== 'cartao' && AbaNovoCartao.Aba}
            {disabled !== 'boleto' && AbaBoleto.Aba}
        </TabContext>
    )
}

function useAbaCartoes() {
    const name = 'cartoes' as const
    const { watch } = useCheckoutForm()

    const customerMyId = watch('Customer.myId')
    const { auth } = useAuth()

    const { data = [] } = cartoes.useFetch({
        enabled: !!customerMyId && auth,
        refetchOnWindowFocus: false,
    })

    const { mutateAsync } = cartoes.mutations.useDelete({
        invalidateQuery: true,
        onSuccess: () => {
            toast.success('Cartão removido com sucesso!')
        },
        onError: (e: any) => {
            toast.error(e.message)
        },
    })

    return {
        Select: (
            <Tab
                label="Cartões salvos"
                value={name}
                icon={<WalletIcon />}
                iconPosition="start"
                className="text-[length:0] sm:text-[length:inherit]"
                title="Cartões salvos"
            />
        ),
        Aba: (
            <TabPanel value={name} sx={{ px: 0 }}>
                <div className="mt-2">
                    <FormFieldSelect
                        name="Card.myId"
                        label="Selecione um cartão"
                        options={data.map(card => ({
                            label: [
                                <span key={card.id}>{card.nome}</span>,
                                <IconButton
                                    onClick={e => {
                                        e.stopPropagation()
                                        mutateAsync(card.id)
                                    }}
                                    key={card.id + 'delete'}
                                    title="Remover cartão"
                                    className="z-1 ml-2"
                                >
                                    <Trash2Icon className="text-red-500" />
                                </IconButton>,
                            ],
                            value: card.myId,
                        }))}
                        placeholder="Selecione um cartão"
                        required
                    />
                </div>
            </TabPanel>
        ),
        name,
        visible: data.length > 0,
    }
}

function useAbaNovoCartao() {
    const name = 'new-creditcard' as const
    const { watch } = useCheckoutForm()
    const card = watch('Card')

    return {
        Select: (
            <Tab
                label="Cartão de crédito"
                value={name}
                icon={<CreditCardIcon />}
                iconPosition="start"
                className="text-[length:0] sm:text-[length:inherit]"
                title="Novo cartão de crédito"
            />
        ),
        Aba: (
            <TabPanel value={name} sx={{ px: 0 }}>
                <div className="mt-2 flex flex-row gap-4">
                    <div className="hidden md:flex items-center justify-center">
                        <Cartao {...card} />
                    </div>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormField
                                name="Card.holder"
                                label="Nome do titular"
                                uppercase
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormField
                                name="Card.number"
                                label="Número do cartão"
                                options={{
                                    creditCard: true,
                                }}
                                required
                            />
                        </Grid>
                        <Grid item sm={8} xs={12}>
                            <FormField
                                name="Card.expiresAt"
                                label="Validade"
                                placeholder="MM/YYYY"
                                options={{
                                    date: true,
                                    datePattern: ['m', 'Y'],
                                }}
                                required
                            />
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <FormFieldMask
                                name="Card.cvv"
                                label="CVV"
                                mask="999"
                                required
                            />
                        </Grid>
                    </Grid>
                </div>
            </TabPanel>
        ),
        name,
    }
}

function useAbaBoleto() {
    const name = 'boleto-pix' as const
    return {
        Select: (
            <Tab
                label="Boleto/PIX"
                value={name}
                icon={<ScanBarcodeIcon />}
                iconPosition="start"
                className="text-[length:0] sm:text-[length:inherit]"
                title="Boleto ou PIX"
            />
        ),
        Aba: (
            <TabPanel value={name} sx={{ px: 0 }}>
                <div className="mt-2">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Alert severity="info" className="text-balance">
                                <AlertTitle>Atenção</AlertTitle>
                                <p>
                                    Você será redirecionado para a o boleto
                                    gerado após a conclusão do contrato, onde
                                    poderá efetuar o pagamento pelo codigo de
                                    barras ou PIX.
                                </p>
                            </Alert>
                        </Grid>
                    </Grid>
                </div>
            </TabPanel>
        ),
        name,
    }
}
